import { Chip, Stack, SxProps, Theme, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  EggAlt,
  Grain,
  SetMeal,
  Grass,
  WaterDrop,
  BakeryDining,
  Spa,
  LocalDining,
  EnergySavingsLeaf,
  FitnessCenter,
  VerifiedUser,
  NoFood,
  Restaurant,
  Favorite,
  Science,
} from '@mui/icons-material';
import {
  DietaryAttribute,
  DietaryCategory,
  DietaryAttributeStatus,
} from '../types';

/** Icon lookup by code, falling back per-category */
const iconByCode: Record<string, typeof EggAlt> = {
  EGG: EggAlt,
  GLUTEN: Grain,
  FISH: SetMeal,
  CELERY: Grass,
  MILK: WaterDrop,
  WHEAT: BakeryDining,
  SOY: Spa,
  SOYA: Spa,
  VEGAN: EnergySavingsLeaf,
  VEGETARIAN: EnergySavingsLeaf,
  HALAL: VerifiedUser,
  KOSHER: VerifiedUser,
  KETO: FitnessCenter,
  PALEO: FitnessCenter,
  LACTOSE: NoFood,
  FODMAP: Science,
};

const categoryFallbackIcon: Record<DietaryCategory, typeof EggAlt> = {
  [DietaryCategory.Allergen]: Spa,
  [DietaryCategory.Intolerance]: NoFood,
  [DietaryCategory.DietaryRequirement]: Restaurant,
  [DietaryCategory.LifestylePreference]: Favorite,
  [DietaryCategory.NutritionalClaim]: LocalDining,
};

const getIcon = (attr: DietaryAttribute) =>
  iconByCode[attr.code.toUpperCase()] ?? categoryFallbackIcon[attr.category];

/** Colour scheme per category */
const categoryColors: Record<DietaryCategory, { border: string; text: string; icon: string }> = {
  [DietaryCategory.Allergen]: { border: 'warning.main', text: 'warning.dark', icon: 'warning.main' },
  [DietaryCategory.Intolerance]: { border: 'error.light', text: 'error.dark', icon: 'error.light' },
  [DietaryCategory.DietaryRequirement]: { border: 'info.main', text: 'info.dark', icon: 'info.main' },
  [DietaryCategory.LifestylePreference]: { border: 'success.main', text: 'success.dark', icon: 'success.main' },
  [DietaryCategory.NutritionalClaim]: { border: 'secondary.main', text: 'secondary.dark', icon: 'secondary.main' },
};

/** Status modifies the chip styling */
const getStatusStyle = (status: DietaryAttributeStatus, category: DietaryCategory) => {
  const base = categoryColors[category];
  switch (status) {
    case DietaryAttributeStatus.Contains:
    case DietaryAttributeStatus.NotSuitable:
      return { borderColor: base.border, color: base.text, iconColor: base.icon };
    case DietaryAttributeStatus.MayContain:
      return { borderColor: 'orange', color: 'text.secondary', iconColor: 'orange' };
    case DietaryAttributeStatus.FreeFrom:
    case DietaryAttributeStatus.Suitable:
    case DietaryAttributeStatus.Certified:
      return { borderColor: 'success.light', color: 'success.dark', iconColor: 'success.main' };
    default:
      return { borderColor: 'grey.400', color: 'text.secondary', iconColor: 'grey.500' };
  }
};

interface DietaryAttributeChipsProps {
  /** Array of dietary attributes to render */
  attributes: DietaryAttribute[];
  /** Filter to specific categories. Empty = show all */
  categories?: DietaryCategory[];
  /** Max chips to show before +N overflow. 0 = show all */
  max?: number;
  /** Chip size */
  size?: 'small' | 'medium';
  /** Show full name instead of code */
  showName?: boolean;
  /** Group visually by category */
  groupByCategory?: boolean;
  /** Spacing between chips (MUI spacing units) */
  spacing?: number;
  /** Container sx overrides */
  sx?: SxProps<Theme>;
}

const lowerFirst = (value: string) => value.charAt(0).toLowerCase() + value.slice(1);

const DietaryAttributeChips = ({
  attributes,
  categories,
  max = 0,
  size = 'small',
  showName = false,
  groupByCategory = false,
  spacing = 1,
  sx,
}: DietaryAttributeChipsProps) => {
  const { t } = useTranslation('recipeV2');
  const filtered = categories?.length
    ? attributes.filter((a) => categories.includes(a.category))
    : attributes;

  const visible = max > 0 ? filtered.slice(0, max) : filtered;
  const overflow = max > 0 && filtered.length > max ? filtered.length - max : 0;
  const iconSize = size === 'small' ? 14 : 18;

  const renderChip = (attr: DietaryAttribute) => {
    const AttrIcon = getIcon(attr);
    const style = getStatusStyle(attr.status, attr.category);
    const label = showName
      ? attr.name
      : attr.code.charAt(0) + attr.code.slice(1).toLowerCase();

    return (
      <Tooltip
        key={`${attr.category}-${attr.code}`}
        title={`${t(`dietary.categories.${lowerFirst(attr.category)}`)}: ${attr.name} (${t(`dietary.${lowerFirst(attr.status)}`)})`}
        arrow
      >
        <Chip
          icon={<AttrIcon sx={{ fontSize: iconSize }} />}
          label={label}
          size={size}
          color="primary"
          variant="outlined"
          sx={{
            px: '1px',
            borderColor: style.borderColor,
            color: style.color,
            fontWeight: 500,
            fontSize: size === 'small' ? '0.7rem' : '0.8rem',
            '& .MuiChip-icon': { color: style.iconColor },
          }}
        />
      </Tooltip>
    );
  };

  if (groupByCategory) {
    const grouped = Object.values(DietaryCategory).reduce(
      (acc, cat) => {
        const items = visible.filter((a) => a.category === cat);
        if (items.length) acc.push({ category: cat, items });
        return acc;
      },
      [] as { category: DietaryCategory; items: DietaryAttribute[] }[],
    );

    return (
      <Stack spacing={1.5}
        sx={sx}>
        {grouped.map(({ category, items }) => (
          <Stack key={category}
            direction="row"
            spacing={spacing}
            flexWrap="wrap"
            useFlexGap
            alignItems="center">
            <Chip
              label={t(`dietary.categories.${lowerFirst(category)}`)}
              size="small"
              color="primary"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                backgroundColor: 'grey.100',
              }}
            />
            {items.map(renderChip)}
          </Stack>
        ))}
        {overflow > 0 && (
          <Chip
            label={t('dietary.moreCount', { count: overflow })}
            size={size}
            color="primary"
            variant="outlined"
            sx={{ fontSize: size === 'small' ? '0.7rem' : '0.8rem', alignSelf: 'flex-start' }}
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack direction="row"
      spacing={spacing}
      flexWrap="wrap"
      useFlexGap
      sx={sx}>
      {visible.map(renderChip)}
      {overflow > 0 && (
        <Chip
          label={`+${overflow}`}
          size={size}
          color="primary"
          variant="outlined"
          sx={{ fontSize: size === 'small' ? '0.7rem' : '0.8rem' }}
        />
      )}
    </Stack>
  );
};

export default DietaryAttributeChips;
